"use client";

import { ArrowUpRight, Loader, MapPin, Minus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const inquiryTypes = [{
  id: 1,
  label: "Aluminum Profiles"
}, {
  id: 2,
  label: "Architectural Hardware"
}, {
  id: 3,
  label: "Bulk Supply"
}, {
  id: 4,
  label: "General Inquiry"
}];
const contactBlocks = [{
  id: 1,
  label: "VISIT",
  detail: "Industrial Area 11, Fourth Street, Sharjah",
  icon: "pin"
}, {
  id: 2,
  label: "CALL",
  detail: "+971-527750-331",
  icon: "minus"
}, {
  id: 3,
  label: "WRITE",
  detail: "info@world-sky.ae",
  icon: "arrow"
}];
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 border border-slate-800 bg-slate-900/50 rounded-lg">
        <p className="font-default text-xl text-white mb-4">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="border-sky-500 text-sky-500 hover:bg-sky-500 hover:text-white rounded-lg">
          Send Another Inquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <FormField control={control} name="name" rules={{
      required: "Name is required",
      minLength: {
        value: 2,
        message: "Min 2 characters"
      }
    }} render={({
      field
    }) => <FormItem className="gap-1">
            <FormLabel className="text-xs uppercase tracking-[0.2em] text-white font-default">
              Full Name
            </FormLabel>
            <FormControl>
              <Input placeholder="John Doe" {...field} className="border-0 border-b border-slate-800 bg-transparent px-0 h-11 focus-visible:ring-0 focus-visible:border-b-2 focus-visible:border-sky-500 text-white placeholder:text-slate-600 font-default text-lg transition-all" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Invalid email"
      }
    }} render={({
      field
    }) => <FormItem className="gap-1">
            <FormLabel className="text-xs uppercase tracking-[0.2em] text-white font-default">
              Corporate Email
            </FormLabel>
            <FormControl>
              <Input type="email" placeholder="john@company.com" {...field} className="border-0 border-b border-slate-800 bg-transparent px-0 h-11 focus-visible:ring-0 focus-visible:border-b-2 focus-visible:border-sky-500 text-white placeholder:text-slate-600 font-default text-lg transition-all" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="phone" render={({
      field
    }) => <FormItem className="gap-1">
            <FormLabel className="text-xs uppercase tracking-[0.2em] text-white font-default">
              Phone Number
            </FormLabel>
            <FormControl>
              <Input type="tel" placeholder="+971 50 123 4567" {...field} className="border-0 border-b border-slate-800 bg-transparent px-0 h-11 focus-visible:ring-0 focus-visible:border-b-2 focus-visible:border-sky-500 text-white placeholder:text-slate-600 font-default text-lg transition-all" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="inquiryType" render={({
      field
    }) => <FormItem className="gap-1">
            <FormLabel className="text-xs uppercase tracking-[0.2em] text-white font-default">
              Inquiry Category
            </FormLabel>
            <FormControl>
              <Select onValueChange={field.onChange} value={field.value}>
                <SelectTrigger className="border-0 border-b border-slate-800 bg-transparent px-0 h-11 w-full focus-visible:ring-0 focus:border-sky-500 text-white font-default text-lg data-[placeholder]:text-slate-600">
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800 rounded-lg">
                  <SelectItem value="aluminum">Aluminum Profiles</SelectItem>
                  <SelectItem value="hardware">Architectural Hardware</SelectItem>
                  <SelectItem value="bulk">Bulk Supply</SelectItem>
                  <SelectItem value="general">General Inquiry</SelectItem>
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="subject" render={({
      field
    }) => <FormItem className="gap-1">
            <FormLabel className="text-xs uppercase tracking-[0.2em] text-white font-default">
              Subject
            </FormLabel>
            <FormControl>
              <Input placeholder="Project requirements" {...field} className="border-0 border-b border-slate-800 bg-transparent px-0 h-11 focus-visible:ring-0 focus-visible:border-b-2 focus-visible:border-sky-500 text-white placeholder:text-slate-600 font-default text-lg transition-all" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Message is required",
      minLength: {
        value: 10,
        message: "Min 10 characters"
      }
    }} render={({
      field
    }) => <FormItem className="gap-1">
            <FormLabel className="text-xs uppercase tracking-[0.2em] text-white font-default">
              Project Details
            </FormLabel>
            <FormControl>
              <Textarea placeholder="Describe your material requirements..." {...field} className="border-0 border-b border-slate-800 bg-transparent px-0 min-h-24 focus-visible:ring-0 focus-visible:border-b-2 focus-visible:border-sky-500 text-white placeholder:text-slate-600 font-default text-lg resize-none transition-all" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 border border-red-500/40 bg-red-500/10 rounded-lg">
          <p className="text-red-500 text-sm font-default">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-14 bg-sky-500 text-white hover:bg-sky-600 rounded-lg uppercase tracking-[0.25em] text-sm font-default font-medium transition-all focus-visible:ring-sky-500/50">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-4" />
            Processing...
          </> : <>
            Submit Inquiry
            <ArrowUpRight className="ml-1 size-4" />
          </>}
      </Button>

      <p className="text-center text-sm text-white font-default">
        Our sales team will contact you within 24 hours.
      </p>
    </Form>;
}
export default function Inquiry() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="4408" id="procurement-inquiry-center" className="bg-slate-950 text-white py-20 md:py-32 px-6 md:px-12 lg:px-20 relative overflow-hidden">
      <div ref={ref} className={`max-w-7xl mx-auto transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          <div className="flex flex-col">
            <span className="text-xs uppercase tracking-[0.4em] text-white font-default mb-8">
              — Procurement Inquiry
            </span>

            <h2 className="font-default font-bold text-5xl md:text-6xl lg:text-7xl leading-[1.05] text-white mb-12">
              Streamlined Procurement
              <br />
              <span className="text-sky-500">Solutions for Your Business</span>
            </h2>

            <div className="flex flex-col">
              {contactBlocks.map((block, i) => <div key={block.id} data-index={i} className="py-8 border-t border-slate-800 first:border-t-0">
                  <div className="flex items-start gap-6">
                    <div className="mt-1 shrink-0">
                      {block.icon === "pin" && <MapPin className="size-5 text-sky-500" strokeWidth={1.5} />}
                      {block.icon === "minus" && <Minus className="size-5 text-sky-500" strokeWidth={1.5} />}
                      {block.icon === "arrow" && <ArrowUpRight className="size-5 text-sky-500" strokeWidth={1.5} />}
                    </div>
                    <div className="flex-1">
                      <span className="text-xs uppercase tracking-[0.3em] text-white font-default block mb-3">
                        {block.label}
                      </span>
                      <p className="font-default text-xl md:text-2xl text-white leading-snug">
                        {block.detail}
                      </p>
                    </div>
                  </div>
                </div>)}
              <div className="border-t border-slate-800" />
            </div>

            <div className="mt-12">
              <span className="text-xs uppercase tracking-[0.3em] text-white font-default block mb-5">
                Supply Categories
              </span>
              <div className="flex flex-wrap gap-3">
                {inquiryTypes.map((type, i) => <Badge key={type.id} data-index={i} variant="outline" className="border border-slate-800 text-white bg-slate-900 px-4 py-1.5 text-xs uppercase tracking-[0.2em] font-default rounded-lg hover:bg-slate-800 transition-colors">
                    {type.label}
                  </Badge>)}
              </div>
            </div>
          </div>

          <div className="lg:pl-8">
            <div className="bg-slate-900/50 border border-slate-800 rounded-lg p-8 md:p-12 shadow-lg">
              <FormProvider formKey="procurement_inquiry_connect_with_sales" formId="procurement_inquiry_connect_with_sales" sectionName="procurement-inquiry-center" defaultValues={{
              name: "",
              email: "",
              phone: "",
              inquiryType: "",
              subject: "",
              message: ""
            }} validationRules={{
              name: {
                required: true,
                minLength: 2
              },
              email: {
                required: true,
                pattern: "email"
              },
              phone: {
                pattern: "phone"
              },
              message: {
                required: true,
                minLength: 10
              }
            }} submitText="Submit Inquiry" successMessage="Inquiry received. Our procurement team will contact you shortly." errorMessage="Submission failed. Please verify your details and try again.">
                <ContactForm />
              </FormProvider>
            </div>
          </div>
        </div>
      </div>
    </section>;
}